import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useAuth } from '@/contexts/AuthContext';
import UploadVideoModal from './components/UploadVideoModal';
import WithdrawModal from './components/WithdrawModal';

const mockVideos = [
  {
    id: 'tv1', title: '高考数学压轴题全解析', category: '高考冲刺', price: 29.9,
    sales: 428, revenue: 12795.2, views: 8920, rating: 4.9, status: 'published',
    thumbnail: 'https://public.readdy.ai/ai/img_res/6925863eccb0f260e16c39a1e41e1d7f.jpg',
    uploadDate: '2026-03-01',
  },
  {
    id: 'tv2', title: '高考语文作文满分技巧', category: '高考冲刺', price: 24.9,
    sales: 312, revenue: 7768.8, views: 6540, rating: 4.8, status: 'published',
    thumbnail: 'https://readdy.ai/api/search-image?query=Chinese%20language%20teacher%20teaching%20essay%20writing%20techniques%20for%20college%20entrance%20exam%2C%20classroom%20with%20writing%20samples%20on%20board%2C%20inspiring%20educational%20atmosphere&width=400&height=280&seq=tv002&orientation=landscape',
    uploadDate: '2026-03-10',
  },
  {
    id: 'tv3', title: '函数与导数专题突破', category: '高考冲刺', price: 26.9,
    sales: 189, revenue: 5084.1, views: 3210, rating: 4.7, status: 'published',
    thumbnail: 'https://readdy.ai/api/search-image?query=Chinese%20high%20school%20math%20teacher%20writing%20function%20and%20derivative%20formulas%20on%20whiteboard%2C%20bright%20classroom%20with%20math%20textbooks%2C%20focused%20academic%20atmosphere&width=400&height=280&seq=tv003&orientation=landscape',
    uploadDate: '2026-03-20',
  },
  {
    id: 'tv4', title: '高考物理电磁学冲刺班', category: '高考冲刺', price: 28.9,
    sales: 0, revenue: 0, views: 0, rating: 0, status: 'review',
    thumbnail: 'https://readdy.ai/api/search-image?query=Chinese%20high%20school%20physics%20teacher%20demonstrating%20electromagnetic%20experiment%2C%20modern%20science%20classroom%20with%20physics%20diagrams%20on%20whiteboard&width=400&height=280&seq=tv004&orientation=landscape',
    uploadDate: '2026-04-10',
  },
];

const withdrawHistory = [
  { id: 'w1', amount: 5000, method: '微信', status: 'success', date: '2026-03-28', account: '微信 138****8888' },
  { id: 'w2', amount: 3000, method: '支付宝', status: 'success', date: '2026-03-15', account: '支付宝 zhang***@qq.com' },
  { id: 'w3', amount: 8000, method: '银行卡', status: 'success', date: '2026-02-28', account: '工商银行 **** 6789' },
];

const salesData = [
  { month: '1月', sales: 45, revenue: 1340 },
  { month: '2月', sales: 78, revenue: 2320 },
  { month: '3月', sales: 156, revenue: 4650 },
  { month: '4月', sales: 89, revenue: 2660 },
];

type DashTab = 'overview' | 'videos' | 'earnings' | 'withdraw';

export default function TeacherDashboardPage() {
  const { profile } = useAuth();
  const [activeTab, setActiveTab] = useState<DashTab>('overview');
  const [showUpload, setShowUpload] = useState(false);
  const [showWithdraw, setShowWithdraw] = useState(false);

  const totalRevenue = mockVideos.reduce((s, v) => s + v.revenue, 0);
  const totalSales = mockVideos.reduce((s, v) => s + v.sales, 0);
  const withdrawn = withdrawHistory.reduce((s, w) => s + w.amount, 0);
  const balance = totalRevenue * 0.8 - withdrawn; // 80% after platform fee

  const maxRevenue = Math.max(...salesData.map(d => d.revenue));

  return (
    <div className="min-h-screen bg-[#f5f4f2]">
      <Navbar />
      <div className="pt-20">
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-800 to-slate-900 text-white">
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 rounded-full bg-amber-400 flex items-center justify-center font-black text-xl text-white">
                  {profile?.name?.[0]?.toUpperCase() || 'T'}
                </div>
                <div>
                  <h1 className="text-xl font-black">{profile?.name || '教师'} 的工作台</h1>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs bg-green-500/20 text-green-400 border border-green-500/30 px-2 py-0.5 rounded-full font-medium">认证教师</span>
                    <span className="text-white/50 text-xs">{profile?.grade || '教学科目未设置'}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowWithdraw(true)}
                className="px-5 py-2.5 bg-amber-500 hover:bg-amber-400 text-white rounded-full text-sm font-bold transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-bank-card-line mr-1.5"></i>提现余额
              </button>
              <button
                onClick={() => setShowUpload(true)}
                className="px-5 py-2.5 bg-red-600 hover:bg-red-500 text-white rounded-full text-sm font-bold transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-upload-cloud-line mr-1.5"></i>上传新课程
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="max-w-7xl mx-auto px-4 md:px-8 flex gap-1 overflow-x-auto pb-0">
            {([
              { key: 'overview', label: '数据概览', icon: 'ri-bar-chart-line' },
              { key: 'videos', label: '我的课程', icon: 'ri-video-line' },
              { key: 'earnings', label: '收益明细', icon: 'ri-money-cny-circle-line' },
              { key: 'withdraw', label: '提现记录', icon: 'ri-bank-card-line' },
            ] as { key: DashTab; label: string; icon: string }[]).map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-1.5 px-5 py-3 text-sm font-semibold whitespace-nowrap cursor-pointer transition-all border-b-2 ${
                  activeTab === tab.key
                    ? 'border-amber-400 text-amber-400'
                    : 'border-transparent text-white/50 hover:text-white/80'
                }`}
              >
                <i className={tab.icon}></i>{tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">

          {/* ── OVERVIEW ── */}
          {activeTab === 'overview' && (
            <div className="flex flex-col gap-8">
              {/* Stats Cards */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { label: '总销售额', value: `¥${totalRevenue.toLocaleString('zh-CN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`, sub: '含平台服务费', icon: 'ri-money-cny-circle-fill', color: 'text-amber-500', bg: 'bg-amber-50' },
                  { label: '可提现余额', value: `¥${balance.toFixed(0)}`, sub: '扣除20%平台费后', icon: 'ri-wallet-3-fill', color: 'text-green-600', bg: 'bg-green-50' },
                  { label: '课程总销量', value: `${totalSales}`, sub: '人次购买', icon: 'ri-shopping-bag-fill', color: 'text-blue-600', bg: 'bg-blue-50' },
                  { label: '已发布课程', value: `${mockVideos.filter(v => v.status === 'published').length}`, sub: `共${mockVideos.length}门课程`, icon: 'ri-video-fill', color: 'text-red-600', bg: 'bg-red-50' },
                ].map(s => (
                  <div key={s.label} className="bg-white rounded-2xl p-5 border border-gray-100">
                    <div className={`w-10 h-10 flex items-center justify-center ${s.bg} rounded-xl mb-3`}>
                      <i className={`${s.icon} ${s.color} text-xl`}></i>
                    </div>
                    <div className={`text-2xl font-black ${s.color} mb-0.5`}>{s.value}</div>
                    <div className="text-xs text-gray-500 font-medium">{s.label}</div>
                    <div className="text-xs text-gray-400 mt-0.5">{s.sub}</div>
                  </div>
                ))}
              </div>

              {/* Revenue Chart */}
              <div className="bg-white rounded-2xl p-6 border border-gray-100">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-black text-gray-900">近期销售趋势</h3>
                  <span className="text-xs text-gray-400">2026年</span>
                </div>
                <div className="flex items-end gap-4 h-40">
                  {salesData.map((d) => (
                    <div key={d.month} className="flex-1 flex flex-col items-center gap-2">
                      <span className="text-xs font-bold text-gray-700">¥{d.revenue}</span>
                      <div className="w-full rounded-t-lg bg-gradient-to-t from-red-600 to-red-400 transition-all" style={{ height: `${(d.revenue / maxRevenue) * 100}%`, minHeight: '8px' }}></div>
                      <span className="text-xs text-gray-500">{d.month}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Top Videos */}
              <div className="bg-white rounded-2xl p-6 border border-gray-100">
                <h3 className="font-black text-gray-900 mb-5">热销课程 Top 3</h3>
                <div className="flex flex-col gap-4">
                  {mockVideos.filter(v => v.status === 'published').slice(0, 3).map((v, i) => (
                    <div key={v.id} className="flex items-center gap-4">
                      <span className={`w-7 h-7 flex items-center justify-center rounded-full text-sm font-black flex-shrink-0 ${i === 0 ? 'bg-amber-400 text-white' : i === 1 ? 'bg-gray-300 text-white' : 'bg-orange-300 text-white'}`}>{i + 1}</span>
                      <img src={v.thumbnail} alt={v.title} className="w-14 h-10 rounded-lg object-cover flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-gray-900 truncate">{v.title}</p>
                        <p className="text-xs text-gray-400">{v.sales} 人购买 · ¥{v.price}/人</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-sm font-black text-green-600">¥{v.revenue.toLocaleString()}</p>
                        <p className="text-xs text-gray-400">总收益</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ── MY VIDEOS ── */}
          {activeTab === 'videos' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-black text-gray-900 text-xl">我的课程（{mockVideos.length}门）</h2>
                <button
                  onClick={() => setShowUpload(true)}
                  className="px-5 py-2.5 bg-red-600 text-white rounded-full text-sm font-bold hover:bg-red-700 transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-add-line mr-1"></i>上传新课程
                </button>
              </div>
              <div className="flex flex-col gap-4">
                {mockVideos.map((v) => (
                  <div key={v.id} className="bg-white rounded-2xl p-5 border border-gray-100 flex flex-col md:flex-row gap-4">
                    <div className="relative w-full md:w-40 h-28 rounded-xl overflow-hidden flex-shrink-0">
                      <img src={v.thumbnail} alt={v.title} className="w-full h-full object-cover" />
                      <div className={`absolute top-2 left-2 text-xs font-bold px-2 py-0.5 rounded-full ${v.status === 'published' ? 'bg-green-500 text-white' : 'bg-amber-400 text-white'}`}>
                        {v.status === 'published' ? '已发布' : '审核中'}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <h3 className="font-black text-gray-900 text-base">{v.title}</h3>
                        <span className="text-red-600 font-black text-base whitespace-nowrap">¥{v.price}</span>
                      </div>
                      <div className="flex flex-wrap gap-3 text-xs text-gray-500 mb-3">
                        <span className="bg-gray-50 px-2 py-1 rounded-lg">{v.category}</span>
                        <span>上传于 {v.uploadDate}</span>
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="text-center bg-gray-50 rounded-xl py-2">
                          <div className="font-black text-gray-900 text-base">{v.sales}</div>
                          <div className="text-xs text-gray-400">销量</div>
                        </div>
                        <div className="text-center bg-gray-50 rounded-xl py-2">
                          <div className="font-black text-green-600 text-base">¥{v.revenue.toLocaleString()}</div>
                          <div className="text-xs text-gray-400">总收益</div>
                        </div>
                        <div className="text-center bg-gray-50 rounded-xl py-2">
                          <div className="font-black text-amber-500 text-base">{v.rating > 0 ? v.rating : '—'}</div>
                          <div className="text-xs text-gray-400">评分</div>
                        </div>
                      </div>
                    </div>
                    <div className="flex md:flex-col gap-2 md:w-24 flex-shrink-0">
                      <button className="flex-1 md:flex-none py-2 px-3 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-xl text-xs font-semibold cursor-pointer transition-colors text-center whitespace-nowrap">
                        <i className="ri-edit-line mr-1"></i>编辑
                      </button>
                      <button className="flex-1 md:flex-none py-2 px-3 bg-red-50 hover:bg-red-100 text-red-600 rounded-xl text-xs font-semibold cursor-pointer transition-colors text-center whitespace-nowrap">
                        <i className="ri-delete-bin-line mr-1"></i>删除
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── EARNINGS ── */}
          {activeTab === 'earnings' && (
            <div className="flex flex-col gap-6">
              {/* Balance Card */}
              <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-3xl p-8 text-white">
                <p className="text-white/60 text-sm mb-1">当前可提现余额</p>
                <div className="text-5xl font-black text-amber-400 mb-2">¥{balance.toFixed(2)}</div>
                <p className="text-white/50 text-xs mb-6">平台收取20%服务费，剩余80%归教师所有</p>
                <div className="flex flex-wrap gap-4 text-sm">
                  <div>
                    <span className="text-white/50">总销售额</span>
                    <span className="text-white font-bold ml-2">¥{totalRevenue.toFixed(0)}</span>
                  </div>
                  <div>
                    <span className="text-white/50">已提现</span>
                    <span className="text-white font-bold ml-2">¥{withdrawn.toLocaleString()}</span>
                  </div>
                  <div>
                    <span className="text-white/50">平台服务费</span>
                    <span className="text-white font-bold ml-2">¥{(totalRevenue * 0.2).toFixed(0)}</span>
                  </div>
                </div>
                <button
                  onClick={() => setShowWithdraw(true)}
                  className="mt-6 px-8 py-3 bg-amber-400 hover:bg-amber-300 text-slate-900 rounded-full font-black text-sm transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-bank-card-line mr-2"></i>立即提现
                </button>
              </div>

              {/* Per-video earnings */}
              <div className="bg-white rounded-2xl p-6 border border-gray-100">
                <h3 className="font-black text-gray-900 mb-5">各课程收益明细</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs text-gray-400 border-b border-gray-100">
                        <th className="text-left pb-3 font-semibold">课程名称</th>
                        <th className="text-right pb-3 font-semibold">单价</th>
                        <th className="text-right pb-3 font-semibold">销量</th>
                        <th className="text-right pb-3 font-semibold">总收益</th>
                        <th className="text-right pb-3 font-semibold">实得（80%）</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {mockVideos.filter(v => v.status === 'published').map(v => (
                        <tr key={v.id}>
                          <td className="py-3 font-medium text-gray-800 max-w-[200px] truncate">{v.title}</td>
                          <td className="py-3 text-right text-gray-600">¥{v.price}</td>
                          <td className="py-3 text-right text-gray-600">{v.sales}</td>
                          <td className="py-3 text-right text-gray-700 font-semibold">¥{v.revenue.toFixed(0)}</td>
                          <td className="py-3 text-right text-green-600 font-black">¥{(v.revenue * 0.8).toFixed(0)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-gray-200">
                        <td className="pt-3 font-black text-gray-900">合计</td>
                        <td></td>
                        <td className="pt-3 text-right font-black text-gray-900">{totalSales}</td>
                        <td className="pt-3 text-right font-black text-gray-900">¥{totalRevenue.toFixed(0)}</td>
                        <td className="pt-3 text-right font-black text-green-600">¥{(totalRevenue * 0.8).toFixed(0)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* ── WITHDRAW HISTORY ── */}
          {activeTab === 'withdraw' && (
            <div className="flex flex-col gap-6">
              <div className="flex items-center justify-between">
                <h2 className="font-black text-gray-900 text-xl">提现记录</h2>
                <button
                  onClick={() => setShowWithdraw(true)}
                  className="px-5 py-2.5 bg-amber-500 text-white rounded-full text-sm font-bold hover:bg-amber-400 transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-bank-card-line mr-1.5"></i>申请提现
                </button>
              </div>
              <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                {withdrawHistory.map((w, i) => (
                  <div key={w.id} className={`flex items-center gap-4 px-6 py-4 ${i < withdrawHistory.length - 1 ? 'border-b border-gray-50' : ''}`}>
                    <div className={`w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 ${
                      w.method === '微信' ? 'bg-green-50' : w.method === '支付宝' ? 'bg-blue-50' : 'bg-gray-50'
                    }`}>
                      <i className={`text-lg ${
                        w.method === '微信' ? 'ri-wechat-pay-fill text-green-600' :
                        w.method === '支付宝' ? 'ri-alipay-fill text-blue-600' :
                        'ri-bank-card-fill text-gray-600'
                      }`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-gray-900">{w.method}提现</p>
                      <p className="text-xs text-gray-400">{w.account} · {w.date}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="font-black text-gray-900">-¥{w.amount.toLocaleString()}</p>
                      <span className="text-xs text-green-600 font-medium bg-green-50 px-2 py-0.5 rounded-full">
                        <i className="ri-check-line mr-0.5"></i>已到账
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 text-sm text-amber-800">
                <i className="ri-information-line mr-2"></i>
                提现通常在1-3个工作日内到账。单次最低提现金额为¥100，每月最多提现5次。
              </div>
            </div>
          )}
        </div>
      </div>

      <Footer />

      {showUpload && <UploadVideoModal onClose={() => setShowUpload(false)} />}
      {showWithdraw && <WithdrawModal balance={balance} onClose={() => setShowWithdraw(false)} />}
    </div>
  );
}
